// Explicit `/index.js` — @heroicons/react v1 ships no `exports` map, so
// strict Node ESM (unlike bundlers) refuses to resolve a bare directory
// import and throws ERR_UNSUPPORTED_DIR_IMPORT.
import {
	ArrowUpIcon,
	ChatAlt2Icon,
	CheckIcon,
	ChevronUpIcon,
	DocumentDownloadIcon,
	DocumentTextIcon,
	DotsHorizontalIcon,
	DuplicateIcon,
	GiftIcon,
	PhotographIcon,
	PlusIcon,
	SparklesIcon,
	TrashIcon,
	XIcon,
} from '@heroicons/react/outline/index.js';

import { Icon, type IconProps } from '@bonusly/design-system';

/**
 * Bizy-kit icon set. Wraps Heroicons (v1 outline) in a Chakra `Icon` so
 * the ported node/card components keep using Chakra style props
 * (`boxSize`, `color`, …) exactly as they did against the design-system
 * icon set. Kept dependency-light: Heroicons is a peer dependency that
 * both Kaleidoscope and Bizy Desktop already ship.
 */
function wrap(glyph: typeof CheckIcon) {
	return function BizyKitIcon(props: IconProps) {
		return <Icon as={glyph} {...props} />;
	};
}

export const ArrowUpOutline = wrap(ArrowUpIcon);
export const ChatAlt2Outline = wrap(ChatAlt2Icon);
export const CheckOutline = wrap(CheckIcon);
export const ChevronUpOutline = wrap(ChevronUpIcon);
export const DocumentDownloadOutline = wrap(DocumentDownloadIcon);
export const DocumentTextOutline = wrap(DocumentTextIcon);
export const DotsHorizontalOutline = wrap(DotsHorizontalIcon);
export const DuplicateOutline = wrap(DuplicateIcon);
export const GiftOutline = wrap(GiftIcon);
export const PhotographOutline = wrap(PhotographIcon);
export const PlusOutline = wrap(PlusIcon);
export const SparklesOutline = wrap(SparklesIcon);
export const TrashOutline = wrap(TrashIcon);
export const XOutline = wrap(XIcon);

// GIF glyph — mirrors the give-box's design-system `GifOutline` so the "Add a
// GIF" affordance reads the same across surfaces (bizy-kit can't import the
// design-system icon set).
export function GifOutline(props: IconProps) {
	return (
		<Icon viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" {...props}>
			<path d="M12.75 6.75V17.25" />
			<path d="M21.375 6.75H16.875V17.25" />
			<path d="M20.25 12H16.875" />
			<path d="M6.75 12.0001H9V14.2501C9 15.0457 8.68393 15.8088 8.12132 16.3714C7.55872 16.934 6.79565 17.2501 6 17.2501C5.20436 17.2501 4.44129 16.934 3.87869 16.3714C3.31608 15.8088 3.00001 15.0457 3.00001 14.2501V9.75005C2.99857 9.01884 3.26423 8.31228 3.74708 7.76315C4.22992 7.21403 4.89669 6.86016 5.62208 6.76805C6.34747 6.67593 7.08153 6.85192 7.68629 7.26294C8.29106 7.67395 8.72489 8.29169 8.90625 9.00005" />
		</Icon>
	);
}

// Doodle (single-stroke scribble) glyph for the "Draw a doodle" affordance.
export function DoodleOutline(props: IconProps) {
	return (
		<Icon viewBox="0 0 24 18" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" {...props}>
			<path d="M3 8.7C3 8.7 6 4.5 10.5 4.5C20.25 4.5 19.1812 19.5 10.1812 19.5C4.18125 19.5 4.18125 10.5 13.1813 10.5C19.1813 10.5 23.25 15 23.25 15" />
		</Icon>
	);
}

// Magic wand — the "Make it special" affordance on the recognition draft card.
// Heroicons v1 has no wand glyph, so this wraps the design's wand SVG (a wand
// with a sparkle star). Uses `stroke="currentColor"` so it inherits the button
// color and works in dark mode, like the other wrapped icons.
export function MagicWandOutline(props: IconProps) {
	return (
		<Icon viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" {...props}>
			<path d="M2.5 17.4999L9.16667 10.8333" />
			<path d="M9.10836 2.69041C9.10017 2.47822 9.34352 2.35291 9.51131 2.48316L11.719 4.20068C12.1969 4.57222 12.8332 4.67025 13.4009 4.46018L16.0235 3.48908C16.223 3.41537 16.4174 3.60904 16.3451 3.80905L15.395 6.43913C15.1892 7.00842 15.2918 7.64384 15.667 8.11887L17.4006 10.3131C17.5324 10.4799 17.4094 10.7244 17.1969 10.7176L14.4015 10.6268C13.7964 10.607 13.2236 10.9016 12.8878 11.4053L11.3367 13.7317C11.2187 13.9087 10.9473 13.8669 10.8882 13.6625L10.1117 10.9761C9.9435 10.3947 9.48698 9.94123 8.90433 9.77742L6.21138 9.02106C6.00672 8.96344 5.96297 8.69222 6.13902 8.5729L8.45432 7.00432C8.95516 6.66472 9.24479 6.09034 9.22057 5.48571L9.10836 2.69041Z" />
		</Icon>
	);
}
